/**
 * RÉFÉRENTIELS → UNITÉS DE MESURE — les unités d'un prix à l'unité (« 9,96 €/kg »).
 *
 * UTILITÉ : montrer au commerçant les unités dans lesquelles un préemballé peut afficher son prix à l'unité, et
 * combien de ses articles emploient chacune. Le calcul lui-même vit dans `core-pricing`
 * (`pricePerReferenceUnitCents`) : cet onglet n'affiche aucun montant.
 *
 * ⚠️ LECTURE SEULE, ET C'EST UNE DÉCISION DE L'UTILISATEUR : la Console sème les sept unités de la
 *    réglementation dans chaque base, le magasin n'en ajoute ni n'en retire. Le dessin porte une ligne d'ajout
 *    et une corbeille — elles ne sont PAS reprises : offrir un geste qu'aucune route ne sert serait un bouton
 *    qui fait semblant (§23.0).
 * ⚠️ « Pièce » N'EST PAS UNE LIGNE DE LA BASE : c'est l'absence d'unité, la vente à la pièce ordinaire. Le
 *    dessin la pose en tête, marquée « Défaut », sans compte — on la rend ainsi.
 * ⚠️ UNE UNITÉ SANS ÉCHELLE DE CALCUL (m², m³) LE DIT : elle s'affiche, mais aucun article ne peut s'y
 *    rattacher — le producteur le refuse.
 */
import type { CSSProperties, ReactNode } from 'react';
import type { MeasurementUnitUsage } from '../api';
import { t } from '../i18n';

const ROW: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12, padding: '0 18px', minHeight: 50, borderBottom: '1px solid var(--ink-100)',
};
const NOTE: CSSProperties = { fontSize: 'var(--t-meta)', color: 'var(--ink-400)', whiteSpace: 'nowrap' };

/** Une ligne de la liste, à la géométrie du dessin : nom, suffixe imprimé, compte, puis la marque de droite. */
function UnitRow({ label, suffix, count, mark }: { label: string; suffix: string; count: string; mark?: ReactNode }) {
  return (
    <div style={ROW}>
      <span style={{ minWidth: 130, fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-900)' }}>{label}</span>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>{suffix}</span>
      <span style={{ flex: 1, textAlign: 'right', fontSize: 'var(--t-body)', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums' }}>{count}</span>
      {mark}
    </div>
  );
}

/**
 * Ce que la ligne d'une unité dit à droite : retirée, ou sans calcul possible — rien sinon.
 * ⚠️ « retirée » L'EMPORTE : une unité que la Console a retirée ne s'emploie plus, qu'elle se calcule ou non.
 */
export function unitMark(u: Pick<MeasurementUnitUsage, 'calculable' | 'isActive'>): string | null {
  if (!u.isActive) return t('retirée — plus proposée');
  if (!u.calculable) return t('ne se calcule pas encore');
  return null;
}

export function MeasurementUnitsTab({ units, failure }: { units: MeasurementUnitUsage[] | null; failure: string | null }) {
  if (failure) {
    return (
      <p role="alert" style={{ margin: 0, padding: '18px 2px', color: 'var(--danger-ink)', fontSize: 'var(--t-body)', lineHeight: 1.6 }}>
        {t('Les unités n’ont pas pu être lues : {failure}. Ce n’est pas « aucune unité ».', { failure })}</p>
    );
  }
  if (!units) return <p style={{ margin: 0, padding: '18px 2px', color: 'var(--ink-500)', fontSize: 'var(--t-body)' }}>{t('Lecture du référentiel…')}</p>;

  return (
    <div style={{ maxWidth: 640 }}>
      <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '15px 18px', borderBottom: '1px solid var(--ink-200)', flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: 150, fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Unités de mesure')}</span>
          <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('Référence d’affichage du prix sur les étiquettes — « le kg », « les 100 g »')}</span>
        </div>
        {/* ⚠️ « Pièce » PORTE SON CONTEXTE : la clé nue est déjà la pièce COMPTABLE (« Document » en anglais), et un
            homonyme hérite en silence du sens de l'autre. */}
        <UnitRow label={t('Pièce|unité de vente')} suffix={t('— sans suffixe')} count=""
          mark={(
            <span style={{
              height: 20, display: 'inline-flex', alignItems: 'center', padding: '0 8px', borderRadius: 'var(--r-pill)',
              background: 'var(--ink-100)', color: 'var(--ink-500)', fontSize: 'var(--t-micro)', fontWeight: 700,
              letterSpacing: '.05em', textTransform: 'uppercase', whiteSpace: 'nowrap',
            }}>{t('Défaut')}</span>
          )} />
        {units.map((u) => {
          const mark = unitMark(u);
          return (
            <UnitRow key={u.code} label={u.label} suffix={`/${u.symbol}`}
              // ⚠️ « libre » DU DESSIN N'EST PAS REPRIS : il annonçait la corbeille, qu'aucune route ne sert ici.
              count={u.models ? t('{n} article', { n: u.models }) : t('aucun article')}
              mark={mark ? <span style={NOTE}>{mark}</span> : undefined} />
          );
        })}
        {/* ⚠️ UNE BASE OÙ LA CONSOLE N'A RIEN SEMÉ LE DIT : une liste réduite à « Pièce » se lirait « le magasin n'a
            aucune unité », alors que c'est la fourniture qui manque. */}
        {units.length === 0 ? (
          <div style={{ ...ROW, color: 'var(--ink-500)', fontSize: 'var(--t-body)' }}>
            {t('Aucune autre unité : la liste n’a pas encore été fournie à ce magasin.')}</div>
        ) : null}
      </div>
      <p style={{ margin: '12px 2px 0', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', lineHeight: 1.55 }}>
        {t('« Pièce » est l’unité par défaut : l’article se vend sans prix à l’unité. Toute autre unité calcule ce prix depuis la contenance de l’article (2,49 € les 250 g → « 9,96 €/kg »). Le choix par article, onglet Prix & marge, et l’impression sur l’étiquette sont à venir.')}
      </p>
    </div>
  );
}
